import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { DragEndEvent } from '@progress/kendo-angular-sortable';
import * as svgIcons from '@progress/kendo-svg-icons';
import { fromEvent, throttleTime } from 'rxjs';
import { DestroyBase } from '../../../../components/common/ng-destroy-base';
import { EventBusService } from '../../../../services/event-bus.service';
import { GuidService } from '../../../project-valuation/services/guid.service';
import { ReportDesignerPage } from '../../models/report-designer-page';
import { ReportDesignerHostComponent } from '../../report-designer-host/report-designer-host.component';
import { ReportDesignerStorageService } from '../../services/report-designer-storage.service';
import { GridsterHelper } from '../../util/gridster-helper';
import { WidgetService } from '../../widgets/widget.service';
import { ReportDesignerDocumentComponent } from '../report-designer-document/report-designer-document.component';
import { ReportDesignerInfobarComponent } from '../report-designer-infobar/report-designer-infobar.component';

/**
 * RD page navigation component
 */
@Component({
  selector: 'report-designer-page-navigation',
  templateUrl: './report-designer-page-navigation.component.html',
  styleUrls: ['./report-designer-page-navigation.component.scss']
})
export class ReportDesignerPageNavigationComponent extends DestroyBase implements OnInit {
  /**
   * The infobar
   */
  @Input() infobar: ReportDesignerInfobarComponent;

  /**
   * The widget host component
   */
  @Input() main: ReportDesignerHostComponent;

  /**
   * The active document
   */
  private activeDocument: ReportDesignerDocumentComponent;
  
  /**
   * The pages
   */
  public pages: ReportDesignerPage[] = [];

  /**
   * The active page's index
   */
  public activePageIndex = 0;

  /**
   * Dragging in progress?
   */
  public dragInProgress = false;

  /**
   * Should the active item be scrolled into view?
   */
  public shouldScrollActiveItemIntoView = false;

  /**
   * Read-only?
   */
  public get isReadOnly(): boolean {
    return !!this.activeDocument?.data?.isReadOnly;
  }

  /**
   * SVG icons
   */
  public allIcons = svgIcons;

  /**
   * Constructor
   * @param eventBus
   * @param guidService
   * @param rdStorage
   * @param widgetService
   * @param elementRef
   */
  constructor(private eventBus: EventBusService,
    private guidService: GuidService,
    private rdStorage: ReportDesignerStorageService,
    private widgetService: WidgetService,
    public elementRef: ElementRef) {
    super();
  }

  /**
   * Set navigation pages
   */
  private setNavigationPages(): void {
    this.activeDocument = this.rdStorage.getActiveDocument();
    this.pages = [...this.activeDocument?.data?.pages || []];//we use copy of original array (but same page objects)!!
  }

  /**
   * Initializes the component
   */
  public ngOnInit(): void {
    this.setNavigationPages();
    this.activePageIndex = 0;

    this.onEvents.push(this.eventBus.subscribeEvent('activeDocumentChanged', () => {
      this.setNavigationPages();
      this.activePageIndex = 0;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('syncToPageList', () => {
      this.setNavigationPages();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('pageDeleted', (index: number) => {
      this.setNavigationPages();
      const pagesCount = this.pages.length;
      if (pagesCount > 0) {
        if (pagesCount > index) {
          //select next page (now with same index)
          this.selectPage(index);
        } else {
          //last page was deleted => select prior page
          this.selectPage(index - 1);
        }
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('pageAdded', (index: number) => {
      this.setNavigationPages();
      this.activePageIndex = index;
      this.setActivePageIndex(index);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('activatePage', (index: number) => {
      this.setActivePageIndex(index);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('pageNavigation:activatePage', (index: number | null) => {
      this.setActivePageIndex(index);
    }));
  }

  /**
   * Sets the active page index
   * @param index
   */
  private setActivePageIndex(index: number) {
    const changed = this.activePageIndex !== index && index !== null;
    this.activePageIndex = index;
    if (changed && this.shouldScrollActiveItemIntoView) {
      this.scrollActiveListItemIntoView();
    }
  }

  /**
   * Select/activate page on given index
   * @param index Page index
   */
  public selectPage(index: number): void {
    const page = this.pages[index];
    if (page.options?.isHidden) {
      //#59646: don't select hidden page
      return;
    }

    this.main.setActivePage(page, true);
    this.scrollToPage(page);
  }

  /**
   * Delete page on given index
   * @param index Page index
   */
  public deletePage(index): void {
    const page = this.pages[index];
    if (!page || (!page.options.canDeleteIfNotEmpty && !(page && page.widgets.length === 0))) {
      return;//no page or widgets are still  on page
    }

    this.eventBus.broadcastEvent("deletePage", index);
  }

  /**
   * Add page to document at given position
   * @param index Page index
   * @param addLandscapePage Add Portrait (false) or Landscape(true) page
   */
  private addPage(index: number, addLandscapePage: boolean): ReportDesignerPage {
    addLandscapePage = typeof (addLandscapePage) === "undefined" ? false : addLandscapePage;

    const page = addLandscapePage ? this.widgetService.getHPageConfig() : this.widgetService.getVPageConfig();
    const pageIndex = index + 1;//after page where "add page" is clicked
    this.activeDocument.addPage(page, pageIndex, true);

    this.scrollToPage(page);

    return page;
  }

  /**
   * Switches the page's orientation: Portrait -> landscape and vice versa
   * @param index Page index
   */
  public switchOrientation(index: number, id: string): void {
    const page = this.pages[index];

    if (page.widgets.length === 0) {
      let addLandscape = page.options.isPortrait;

      if (typeof (addLandscape) == 'undefined') {
        addLandscape = page.options.name.indexOf("-H") === -1;
      }

      const deletedPageIndex = this.pages.indexOf(this.pages.find(page => page.options.id == id));
      this.deletePage(deletedPageIndex);

      setTimeout(() => {
        const page = this.addPage(index - 1, addLandscape);//after deleted page
        this.scrollToPage(page);
      });
    }
  }

  /**
   * onDragStart event handler
   */
  public onDragStart(): void {
    this.dragInProgress = true;

    //sometimes kendo sortable "forget" to call onDragEnd (some internal uncaught exception)
    //so capture mouseup to reset dragInProgress flag
    const mouseUpEvent$ = fromEvent(document, 'mouseup', { capture: true, passive: true, once: true });
    const sub = mouseUpEvent$.pipe(throttleTime(100)).subscribe(() => {
      this.dragInProgress = false;
      sub.unsubscribe();
    });
  }

  /**
   * onDragEnd event handler
   * @param e DragEndEvent
   */
  public onDragEnd(e: DragEndEvent): void {
    this.dragInProgress = false;

    const newIndex = e.index;
    const oldIndex = e.oldIndex;
    
    if (newIndex !== oldIndex && newIndex >= 0) {
      this.activeDocument.movePage(oldIndex, newIndex);
      this.activePageIndex = newIndex;
      const page = this.pages[this.activePageIndex];
      this.scrollToPage(page);
    }
  }

  /**
   * Show/hide page at given position
   * @param index Page index
   */
  public toggleShowPage(index: number): void {
    this.eventBus.broadcastEvent("hideOrShowPage", index);
  }

  /**
   * Toggles between normal "page view" to "overview mode" (when all pages are displayed as thumbs)
   */
  public toggleReportOverviewMode(): void {
    this.eventBus.broadcastEvent("toggleReportOverviewMode");
  }

  /**
   * Adds an appendix page to document at given position
   * @param index Page index
   */
  public addAppendixPage(index: number) {
    const page: ReportDesignerPage = {
      options: {
        name: "size-a4-z150",
        isPortrait: true,
        margins: [0, 5],
        columns: 6,
        minRows: 96,
        maxRows: 96,
        colWidth: 187.5,
        rowHeight: 15,
        floating: false,
        swapping: false,
        pushing: false,
        isSpecialPage: true,
        isAppendixPage: true,
        disableSwitchOrientation: true,
        canDeleteIfNotEmpty: true,
        isOnDisplay: true,
        isHidden: false,
        draggable: {
          enabled: false
        },
        resizable: {
          enabled: false
        }
      },
      widgets: []
    };

    const guid = this.guidService.generate();

    page.widgets = [
      {
        title: 'Appendix Page',
        widgetId: -100003,
        col: 0,
        row: 0,
        supportsPortrait: true,
        supportsLandscape: false,
        sizeX: page.options.columns,
        sizeY: page.options.maxRows,
        minSizeX: page.options.columns,
        maxSizeX: page.options.columns,
        minSizeY: page.options.minRows,
        maxSizeY: page.options.maxRows,
        template: "<appendix-page-widget widget-config='widget'></appendix-page-widget>",
        noComment: true,
        noDelete: true,
        widgetSettings: {
          id: guid,
          currentLayout: guid,
          enabledButtonInfobar: true,
          disableInfobarActions: true
        },
        multipleInstances: true
      }
    ];

    GridsterHelper.updatePageConfigForGridster2(page.options);
    GridsterHelper.updateWidgetForGridster2(page.widgets[0], page.options);

    const pageIndex = index + 1;//after page where "add page" is clicked
    this.activeDocument.addPage(page, pageIndex, true);
    this.scrollToPage(page);
  }

  /**
   * Scroll main RD view to some page
   * @param page Page
   */
  private scrollToPage(page: ReportDesignerPage): void {
    setTimeout(() => {
      this.activeDocument.scrollToPage(page);
    });
  }

  /**
   * Scroll active list item into view
   * @param page Page
   */
  private scrollActiveListItemIntoView(): void {
    //wait for redraw
    setTimeout(() => {
      const containerEl = this.elementRef.nativeElement as HTMLElement;
      const wrapperEl: HTMLElement = containerEl.querySelector(`.page-wrapper`);
      const topItemEl: HTMLElement = containerEl.querySelector(`.pagelist-topitem-container`);
      const scrollableEl: HTMLElement = containerEl.querySelector(`.pagelist`);
      const listItemEl: HTMLElement = containerEl.querySelector(`.PagelistItem-${this.activePageIndex}`);

      if (listItemEl) {
        if (this.activePageIndex === 0) {
          wrapperEl.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
          })
        } else if (!this.isElementInViewport(wrapperEl, topItemEl, scrollableEl, listItemEl)) {
          listItemEl.scrollIntoView();
        }
      }
    });
  }

  /**
   * Checks if element is in viewport (visible on screen)
   * @param wrapperEl HTMLElement
   * @param topItemEl HTMLElement
   * @param scrollableEl HTMLElement
   * @param listItemEl HTMLElement
   */
  private isElementInViewport(wrapperEl: HTMLElement, topItemEl: HTMLElement, scrollableEl: HTMLElement, listItemEl: HTMLElement) {
    const visibleScrollHeight = wrapperEl.clientHeight - topItemEl.clientHeight;//f.e. 1149
    const scrollableTop = Math.abs(scrollableEl.getBoundingClientRect().top);//f.e. 6600
    const scrollableBottom = scrollableTop + visibleScrollHeight;

    const listItemTop = listItemEl.offsetTop;//f.e.7000
    const listItemHeight = listItemEl.offsetHeight;//e.g.144
    const deltaHeight = listItemHeight / 2;

    const visible = listItemTop >= (scrollableTop - deltaHeight) && listItemTop < (scrollableBottom + deltaHeight);

    return visible;
  }
}
